import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ClipboardList, Plus, Edit, Trash2, Loader2, Calendar, Clock, FileSpreadsheet, AlertTriangle, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
import { PersonAvatar } from '../components/PersonAvatar';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useConfirmation } from '../components/ConfirmationProvider';
import { toLocalISODate } from '../lib/formatUtils';
import { exportToExcel } from '../lib/exportUtils';
import { usePageSize } from '../context/PageSizeContext';
import { Pagination } from '../components/Pagination';

export const EODNotesPage = () => {
  const { user, hasPermission } = useAuth();
  const confirm = useConfirmation();
  const { pageSize } = usePageSize();
  const isSystemUser = !user?.is_employee;
  // HR-access employees (or any role with eod_notes.view_all) see the team feed too
  const canViewAll = isSystemUser || hasPermission('eod_notes.view_all');
  const [notes, setNotes] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [formDate, setFormDate] = useState(toLocalISODate());
  const [formNote, setFormNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [filterDate, setFilterDate] = useState('');
  // 'all' = entire team, 'mine' = just my own, or a specific employee id
  const [filterEmployee, setFilterEmployee] = useState('all');
  // Feb 2026 — date-range filter alongside the existing single-day picker.
  // `filterDateTo` is empty by default → single-day mode (backward compat).
  // Setting a `filterDateTo` switches the API to `date_from`/`date_to`.
  const [filterDateTo, setFilterDateTo] = useState('');
  const [employees, setEmployees] = useState([]);
  // Missing-EOD-Notes banner state (admins / eod_notes.view_all only).
  // We first resolve the previous working day from the backend, then
  // seed `filterDate` with it so the feed below and the banner stay in
  // sync (user choice 5a + 1a).
  const [missingEod, setMissingEod] = useState({ data: [], total: 0, date: '', loaded: false, all_caught_up: false });
  const [missingExpanded, setMissingExpanded] = useState(true);
  const [autoSeeded, setAutoSeeded] = useState(false);

  useEffect(() => {
    if (canViewAll) {
      api.get('/employees?status=active&limit=500').then(res => {
        if (res.data.success) setEmployees(res.data.data);
      }).catch(() => {});
    }
  }, [canViewAll]);

  // Fetch the "missing EOD" list — when the page first opens, the
  // backend auto-resolves the previous working day (skipping weekends
  // + holidays), and we mirror that date into `filterDate` so the
  // notes feed shows the same day the banner is talking about.
  const fetchMissing = useCallback(async (dateOverride) => {
    if (!canViewAll) return;
    try {
      const qs = dateOverride ? `?date=${dateOverride}` : '';
      const res = await api.get(`/eod-notes/missing${qs}`);
      if (res.data?.success) {
        setMissingEod({
          data: res.data.data || [],
          total: res.data.total || 0,
          date: res.data.date,
          loaded: true,
          all_caught_up: !!res.data.all_caught_up,
        });
        // On first load only, sync the page filter to the resolved date.
        if (!autoSeeded && !filterDate) {
          setFilterDate(res.data.date);
          setAutoSeeded(true);
        }
      }
    } catch {/* permission gate; non-fatal */}
  }, [canViewAll, autoSeeded, filterDate]);

  useEffect(() => { fetchMissing(); }, [fetchMissing]);

  // When the user changes the date filter manually, re-run the missing
  // check for that date too (keeps banner and feed in sync — choice 1a).
  useEffect(() => {
    if (canViewAll && autoSeeded && filterDate) {
      fetchMissing(filterDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDate, canViewAll, autoSeeded]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [filterDate, filterDateTo, filterEmployee]);

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(pageSize) });
      if (canViewAll) {
        // Prefer range-mode (`date_from`/`date_to`) whenever an end date
        // is set. Single-day mode remains the default so existing links
        // and bookmarks continue to work unchanged.
        if (filterDateTo) {
          if (filterDate) params.set('date_from', filterDate);
          params.set('date_to', filterDateTo);
        } else if (filterDate) {
          params.set('date', filterDate);
        }
        if (filterEmployee === 'mine') params.set('mine', 'true');
        else if (filterEmployee && filterEmployee !== 'all') params.set('employee_id', filterEmployee);
        const res = await api.get(`/eod-notes/all?${params.toString()}`);
        if (res.data.success) {
          setNotes(res.data.data);
          setTotal(res.data.total || 0);
        }
      } else {
        const res = await api.get(`/eod-notes?${params.toString()}`);
        if (res.data.success) {
          setNotes(res.data.data);
          setTotal(res.data.total || 0);
        }
      }
    } catch {}
    finally { setLoading(false); }
  }, [canViewAll, filterDate, filterDateTo, filterEmployee, page, pageSize]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ limit: '10000' });
      let url;
      if (canViewAll) {
        if (filterDateTo) {
          if (filterDate) params.set('date_from', filterDate);
          params.set('date_to', filterDateTo);
        } else if (filterDate) {
          params.set('date', filterDate);
        }
        if (filterEmployee === 'mine') params.set('mine', 'true');
        else if (filterEmployee && filterEmployee !== 'all') params.set('employee_id', filterEmployee);
        url = `/eod-notes/all?${params.toString()}`;
      } else {
        url = `/eod-notes?${params.toString()}`;
      }
      const res = await api.get(url);
      const rows = res.data?.data || [];
      if (!rows.length) { toast.info('No notes to export'); return; }
      exportToExcel(rows.map(n => ({
        date: n.date,
        employee_name: n.employee_name || '',
        note: n.note,
        created_at: n.created_at,
      })), [
        { key: 'date', header: 'Date' },
        { key: 'employee_name', header: 'Employee' },
        { key: 'note', header: 'Note' },
        { key: 'created_at', header: 'Submitted' },
      ], 'EOD_Notes');
      toast.success(`Exported ${rows.length} notes`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Export failed'); }
    finally { setExporting(false); }
  };

  const handleSubmit = async () => {
    if (!formNote.trim()) { toast.error('Please enter your EOD note'); return; }
    setSaving(true);
    try {
      if (editingNote) {
        await api.put(`/eod-notes/${editingNote.id}`, { note: formNote });
        toast.success('Note updated');
      } else {
        await api.post('/eod-notes', { note: formNote, date: formDate });
        toast.success('EOD note submitted');
      }
      setShowDialog(false);
      setFormNote('');
      setEditingNote(null);
      fetchNotes();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save note'); }
    finally { setSaving(false); }
  };

  const handleEdit = (note) => {
    setEditingNote(note);
    setFormDate(note.date);
    setFormNote(note.note);
    setShowDialog(true);
  };

  const handleDelete = async (note) => {
    const confirmed = await confirm({ title: 'Delete Note', description: 'Are you sure you want to delete this EOD note?' });
    if (!confirmed) return;
    try {
      await api.delete(`/eod-notes/${note.id}`);
      toast.success('Note deleted');
      fetchNotes();
    } catch { toast.error('Failed to delete'); }
  };

  const openNew = () => {
    setEditingNote(null);
    setFormDate(toLocalISODate());
    setFormNote('');
    setShowDialog(true);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="eod-notes-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">EOD Task Notes</h1>
          <p className="text-muted-foreground">
            {canViewAll ? 'View team EOD task summaries and add your own' : 'Submit your daily task summary'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting} data-testid="eod-export-btn">
            {exporting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-1" />}
            Excel
          </Button>
          {canViewAll && (
            <>
              <Select value={filterEmployee} onValueChange={setFilterEmployee}>
                <SelectTrigger className="w-[200px]" data-testid="eod-filter-employee"><SelectValue placeholder="All Employees" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Employees</SelectItem>
                  <SelectItem value="mine">My notes only</SelectItem>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="date"
                value={filterDate}
                onChange={e => setFilterDate(e.target.value)}
                className="w-[160px]"
                data-testid="eod-filter-date"
                title="From date (single-day when To is empty)"
              />
              <span className="text-xs text-muted-foreground">to</span>
              <Input
                type="date"
                value={filterDateTo}
                onChange={e => setFilterDateTo(e.target.value)}
                className="w-[160px]"
                data-testid="eod-filter-date-to"
                title="To date — leave empty for single-day view"
                placeholder="To (optional)"
              />
              {filterDateTo && (
                <Button
                  variant="ghost" size="sm"
                  onClick={() => setFilterDateTo('')}
                  data-testid="eod-filter-date-to-clear"
                  title="Clear range — back to single day"
                >Clear range</Button>
              )}
            </>
          )}
          <Button onClick={openNew} data-testid="eod-add-note-btn">
            <Plus className="h-4 w-4 mr-2" />Add EOD Note
          </Button>
        </div>
      </div>

      {/* Missing EOD Notes banner — visible to reviewers only.
          Shows who skipped submission for the resolved previous-working-day
          (or the manually-selected date). Clicking a badge deep-links to
          that employee's profile so the reviewer can follow up. */}
      {canViewAll && missingEod.loaded && (
        <Card
          className={
            missingEod.all_caught_up
              ? 'border-emerald-500/40 bg-emerald-500/5'
              : 'border-amber-500/40 bg-amber-500/5'
          }
          data-testid="eod-missing-banner"
        >
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                {missingEod.all_caught_up ? (
                  <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-sm" data-testid="eod-missing-banner-title">
                      {missingEod.all_caught_up
                        ? 'All caught up — no missing EOD notes'
                        : `${missingEod.total} ${missingEod.total === 1 ? 'employee has' : 'employees have'} not submitted an EOD note`}
                    </p>
                    {missingEod.date && (
                      <Badge variant="outline" className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(missingEod.date + 'T00:00:00').toLocaleDateString('en-IN', {
                          weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
                        })}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {missingEod.all_caught_up
                      ? 'Every active employee on duty that day submitted their note.'
                      : 'Excludes employees on approved leave, holidays, and their weekly off days. Click a name to open the profile.'}
                  </p>

                  {!missingEod.all_caught_up && missingExpanded && missingEod.data.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2" data-testid="eod-missing-employee-list">
                      {missingEod.data.map(emp => (
                        <Link
                          key={emp.id}
                          to={`/employees/${emp.id}`}
                          data-testid={`eod-missing-emp-${emp.id}`}
                          className="inline-flex"
                        >
                          <Badge
                            variant="outline"
                            className="flex items-center gap-1.5 pl-1 pr-2 py-0.5 hover:bg-amber-500/15 transition-colors cursor-pointer"
                          >
                            <PersonAvatar
                              firstName={emp.first_name}
                              lastName={emp.last_name}
                              picture={emp.profile_picture}
                              seed={emp.id}
                              size="xs"
                            />
                            {emp.first_name} {emp.last_name}
                            {emp.designation && (
                              <span className="text-muted-foreground text-[10px] ml-1">
                                · {emp.designation}
                              </span>
                            )}
                          </Badge>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {!missingEod.all_caught_up && missingEod.data.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setMissingExpanded(v => !v)}
                  data-testid="eod-missing-toggle"
                  className="shrink-0"
                >
                  {missingExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : notes.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <ClipboardList className="h-12 w-12 mb-4 opacity-40" />
            <p className="text-lg font-medium">No EOD notes found</p>
            <p className="text-sm">
              {canViewAll ? 'No EOD notes match the current filters.' : 'Start by adding your first daily task summary.'}
            </p>
            <Button className="mt-4" onClick={openNew} data-testid="eod-empty-add-btn">
              <Plus className="h-4 w-4 mr-2" />Add Note
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {notes.map(note => (
            <Card key={note.id} data-testid={`eod-note-${note.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <Badge className="bg-primary/10 text-primary flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(note.date + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </Badge>
                      {canViewAll && note.employee_name && (() => {
                        // Deep-link to the employee profile when we have an
                        // `employee_id`. System-user authors (no employee
                        // link) render as a non-interactive chip.
                        const chip = (
                          <Badge
                            variant="outline"
                            className={
                              'flex items-center gap-1.5 pl-1 pr-2 py-0.5' +
                              (note.employee_id ? ' hover:bg-primary/10 transition-colors cursor-pointer' : '')
                            }
                          >
                            <PersonAvatar
                              firstName={note.first_name || note.employee_name?.split(' ')[0] || ''}
                              lastName={note.last_name || note.employee_name?.split(' ').slice(1).join(' ') || ''}
                              picture={note.profile_picture}
                              seed={note.employee_id || note.user_id || note.employee_name}
                              size="xs"
                            />
                            {note.employee_name}
                          </Badge>
                        );
                        return note.employee_id ? (
                          <Link
                            to={`/employees/${note.employee_id}`}
                            data-testid={`eod-note-emp-link-${note.id}`}
                            className="inline-flex"
                          >
                            {chip}
                          </Link>
                        ) : chip;
                      })()}
                      {(() => {
                        // Backdated indicator: visible only to reviewers
                        // (system users + employees with eod_notes.view_all).
                        // Compares the note's `date` (YYYY-MM-DD, IST-implicit)
                        // with its `created_at` date — if the note was filed
                        // for an earlier day than when it was actually created,
                        // surface it so auditors can flag late entries.
                        if (!canViewAll) return null;
                        if (!note.date || !note.created_at) return null;
                        const createdDay = toLocalISODate(new Date(note.created_at));
                        if (note.date >= createdDay) return null;
                        const daysLate = Math.round(
                          (new Date(createdDay) - new Date(note.date)) / (24 * 60 * 60 * 1000)
                        );
                        return (
                          <Badge
                            variant="outline"
                            className="border-amber-500/40 bg-amber-500/10 text-amber-500 flex items-center gap-1"
                            title={`Filed ${daysLate} day(s) after the work date`}
                            data-testid={`eod-backdated-${note.id}`}
                          >
                            <Clock className="h-3 w-3" />
                            Backdated{daysLate > 1 ? ` (${daysLate}d late)` : ''}
                          </Badge>
                        );
                      })()}
                    </div>
                    <p className="text-sm whitespace-pre-wrap" data-testid={`eod-note-text-${note.id}`}>{note.note}</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      Submitted: {new Date(note.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short', hour12: true })}
                      {note.updated_at && note.created_at &&
                        // tolerate sub-second drift from legacy records where
                        // created_at and updated_at were captured separately
                        Math.abs(new Date(note.updated_at) - new Date(note.created_at)) > 2000
                        && ' (edited)'}
                    </p>
                  </div>
                  {note.user_id === user?.id && (() => {
                    const createdAt = new Date(note.created_at);
                    const isWithin24h = (Date.now() - createdAt.getTime()) < 24 * 60 * 60 * 1000;
                    if (!isWithin24h) return null;
                    return (
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(note)} data-testid={`eod-edit-${note.id}`}>
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(note)} data-testid={`eod-delete-${note.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    );
                  })()}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Pagination
        page={page}
        total={total}
        pageSize={pageSize}
        onPageChange={setPage}
        testIdPrefix="eod-pagination"
      />

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-primary" />
              {editingNote ? 'Edit EOD Note' : 'Add EOD Note'}
            </DialogTitle>
            <DialogDescription>
              Summarize the tasks you completed today
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Date</Label>
              <Input
                type="date"
                value={formDate}
                onChange={e => setFormDate(e.target.value)}
                disabled={!!editingNote}
                data-testid="eod-form-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Tasks Completed *</Label>
              <Textarea
                value={formNote}
                onChange={e => setFormNote(e.target.value)}
                placeholder="- Completed API integration for invoices&#10;- Fixed login page bug&#10;- Reviewed PR #42&#10;- Attended client meeting"
                rows={8}
                data-testid="eod-form-note"
              />
              <p className="text-xs text-muted-foreground">Use bullet points or line breaks to list your tasks</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={saving} data-testid="eod-submit-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {editingNote ? 'Update' : 'Submit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
